import React from 'react';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import { addUrlProps, replaceInUrlQuery } from 'react-url-query';

function decodeFilter (url) {
  const urlStringToFilter = (url) => url
    .split('-AND-')
    .map(v => {
      const parts = v.split('_eq_');
      return { identifier: parts[0], value: parts[0], selected: parts[1] === '1' };
    });
  const filterParams = Object.keys(url)
    .filter(key => key.indexOf('filter-') === 0)
    .map(key => {
      const filterName = key.split('-').slice(1).join('-');
      return { [filterName]: urlStringToFilter(url[key]) };
    })
    .reduce((acc, v) => {
      return { ...acc, ...v };
    }, {});
  return filterParams;
}

function mapUrlToProps (url, props) {
  return {
    urlFilters: decodeFilter(url)
  };
}

function mapUrlChangeHandlersToProps (props) {
  return {
    onChangeUrlFilters: (filters) => {
      Object
        .keys(filters)
        .forEach(key => {
          const val = filters[key];
          if (val.length === 0) {
            return;
          }

          if (Object.prototype.hasOwnProperty.call(val[0], 'selected')) {
            const urlString = val
              .map(v => v.identifier + '_eq_' + (v.selected ? 1 : 0))
              .join('-AND-');
            replaceInUrlQuery('filter-' + key, urlString);
          } else {
            const val = filters[key];
            const urlString = val
              .map(v => v.identifier)
              .join(',');
            replaceInUrlQuery('filterarr-' + key, urlString);
          }
        });
    }
  };
}

export function withFilters (WrappedComponent, selectData) {
  class WithFilters extends React.Component {
    constructor (props) {
      super(props);
      this.handleGraphQLInputChange = this.handleGraphQLInputChange.bind(this);
      this.handleGraphQLCheckboxInputChange = this.handleGraphQLCheckboxInputChange.bind(this);
      this.handlePagination = this.handlePagination.bind(this);
      this.getFiltersForGraphQL = this.getFiltersForGraphQL.bind(this);
      this.refetchDebounced = debounce(props.relay.refetch, 750);
      this.handleOrderChange = this.handleOrderChange.bind(this);

      this.state = {
        offset: props.offset || 0,
        limit: props.limit || 20,
        filters: props.filters || {},
        order: props.order || {},
        arrayStringFilters: props.urlFilters || {},
        loading: false
      };
    }

    handleGraphQLInputChange (name) {
      const change = (event, action, index) => {
        const value = typeof event === 'string' ? event : event.target.value;
        const defaultEntry = { string: '', operator: 'CONTAINS' };

        this.setState((state, props) => {
          let newFilter = cloneDeep(state.filters[name]) || { operator: 'AND', values: [defaultEntry] };
          if (action === 'containsOperator') {
            newFilter.values[index] = {
              operator: value,
              string: get(state, ['filters', name, 'values', index, 'string'], '')
            };
          } else if (action === 'booleanOperator') {
            newFilter.operator = value;
          } else if (action === 'delete') {
            newFilter.values.splice(index, 1);
          } else if (action === 'add') {
            newFilter.values = newFilter.values.concat([defaultEntry]);
          } else if (action === 'reset') {
            newFilter = { operator: 'AND', values: [defaultEntry] };
          } else if (action === 'changeString') {
            newFilter.values[index] = {
              string: value,
              operator: get(state, ['filters', name, 'values', index, 'operator'], 'CONTAINS')
            };
          }

          return { offset: 0, filters: Object.assign({}, state.filters, { [name]: newFilter }) };
        });
      };

      return {
        changeBooleanOperator: event => change(event, 'booleanOperator'),
        changeContainsOperator: index => event => change(event, 'containsOperator', index),
        deleteInput: index => event => change(event, 'delete', index),
        clearString: index => event => change(event, 'clearString', index),
        addInput: event => change(event, 'add'),
        changeString: index => event => change(event, 'changeString', index),
        reset: event => change(event, 'reset')
      };
    }

    handleOrderChange (name) {
      return (order) => {
        this.setState((state, props) => ({ offset: 0, order: Object.assign({}, state.order, { [name]: order }) }));
      };
    }

    refetchFromGraphQL () {
      this.setState({ loading: true });
      const refetchVariablesFunction = fragmentVariables => ({
        ...fragmentVariables,
        host: this.props.project.host,
        offset: this.state.offset,
        limit: this.state.limit,
        ...this.state.order,
        ...this.getFiltersForGraphQL()
      });

      this.refetchDebounced(refetchVariablesFunction, null, (error) => {
        if (error) {
          throw new Error(error);
        }
        this.setState({ loading: false });
      });
    }

    componentDidUpdate (prevProps, prevState) {
      const { loading: prevLoading, ...prevStateToCheck } = prevState;
      const { loading, ...stateToCheck } = this.state;

      if (!isEqual(prevState.arrayStringFilters, this.state.arrayStringFilters)) {
        this.props.onChangeUrlFilters(this.state.arrayStringFilters);
      }

      if (!isEqual(prevStateToCheck, stateToCheck)) {
        this.refetchFromGraphQL();
      }
    }

    componentDidMount () {
      if (Object.keys(this.state.arrayStringFilters).length > 0) {
        this.refetchFromGraphQL();
      }
    }

    handleGraphQLCheckboxInputChange (filterName, mapping, sendAsObject = false) {
      return event => {
        const checkboxName = event.target && event.target.name;
        this.setState(oldState => {
          let initializedStateForFilter = oldState.arrayStringFilters[filterName];
          if (initializedStateForFilter === undefined || checkboxName === undefined) {
            // init this state
            initializedStateForFilter = mapping.map(v => {
              return { identifier: v.identifier, value: v.value, selected: true, sendAsObject };
            });
          }

          const nextStateForFilter = initializedStateForFilter
            .map(v => ({
              sendAsObject,
              identifier: v.identifier,
              value: v.identifier,
              selected: v.identifier.toString() === checkboxName ? !v.selected : v.selected
            }));
          const newFilter = Object.assign({}, oldState.arrayStringFilters, { [filterName]: nextStateForFilter });
          return { arrayStringFilters: newFilter, offset: 0 };
        });
      };
    }

    handlePagination ({ offset }) {
      return this.setState({ offset });
    }

    getFiltersForGraphQL () {
      const filters = this.state.arrayStringFilters;
      const arrayFiltersInGraphFormat = Object.entries(filters).reduce((acc, val) => {
        const filterName = val[0];
        const values = val[1];
        acc[filterName] = values
          .filter(v => v.sendAsObject || v.selected)
          .map(v => v.sendAsObject ? { [v.identifier]: v.selected } : v.value);

        // we either send an array of strings as filter to graph or
        // an object looking like {value1: true, value2: false}
        if (typeof acc[filterName][0] === 'object') {
          acc[filterName] = acc[filterName].reduce((acc, val) => {
            return Object.assign({}, acc, val);
          }, {});
        }
        return acc;
      }, {});

      const stringFilters = Object.entries(this.state.filters)
        .map(([key, v]) => ({
          [key]: {
            operator: v.operator,
            values: v.values.map(value => ({ operator: value.operator, value: value.string }))
          }
        }))
        .reduce((acc, val) => ({ ...acc, ...val }), {});

      return Object.assign({}, arrayFiltersInGraphFormat, stringFilters);
    }

    render () {
      return (
        <WrappedComponent
          onPagination={this.handlePagination}
          onGraphQLInputChange={this.handleGraphQLInputChange}
          onGraphQLCheckboxInputChange={this.handleGraphQLCheckboxInputChange}
          onOrderChange={this.handleOrderChange}
          loading={this.state.loading}
          filters={{ ...this.state.arrayStringFilters, ...this.state.filters }}
          limit={this.state.limit}
          offset={this.state.offset}
          order={this.state.order}
          {...this.props}
        />
      );
    }
  }
  return addUrlProps({ mapUrlToProps, mapUrlChangeHandlersToProps })(WithFilters);
}
